import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  PropsWithChildren,
  useCallback,
  useRef,
  FC,
} from "react";
import keycloak from "src/core/keycloak"; // Your Keycloak instance
import Keycloak, { KeycloakProfile } from "keycloak-js";
import Loader from "../components/loader";
import { AccountService } from "src/services/account.service";
import { Organization } from "src/types/OrganizationDTO";
import { caslStore } from "../stores/store"; // Use shared caslStore instance from store context
import { membershipRoleToCapabilities } from "../constants/capability-roles";
import type {
  BootstrapSubscription,
  BootstrapCurrentOrg,
  BootstrapMembership,
} from "src/types/BootstrapDTO";

// Define User shape
interface User {
  id?: string;
  username?: string;
  email?: string;
  roles: string[];
  token?: string;
}

// Subscription status for current organization (from bootstrap)
interface CurrentOrgSubscription {
  subscription?: BootstrapSubscription;
  needsPayment: boolean;
  checkoutUrl?: string;
  membershipRole?: string;
}

// Define the context value shape
interface AuthContextValue {
  keycloakInstance: Keycloak | null;
  authenticated: boolean;
  organizations: Organization[];
  user: User | null;
  loading: boolean;
  noOrgSelected: boolean;
  /** Current organization subscription status (from bootstrap) */
  currentOrgSubscription: CurrentOrgSubscription | null;
  /** Full list of memberships returned by bootstrap (contains per-org subscription info) */
  memberships: BootstrapMembership[];
  login: () => void;
  logout: () => void;
  refreshOrganizations: () => Promise<void>;
  refreshPermissions: () => Promise<void>;
  /** Refresh bootstrap data (organizations + subscription status) */
  refreshBootstrap: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

// Hook to access the AuthContext
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
type AuthProviderProps = PropsWithChildren<{
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  initOptions?: Keycloak.KeycloakInitOptions;
  onAuthSuccess?: () => void;
  onAuthError?: (error: Error) => void;
  onTokenExpired?: () => void;
}>;

// The Provider component
export const AuthProvider: FC<AuthProviderProps> = ({
  children,
  initOptions = {
    onLoad: "login-required",
    pkceMethod: "S256",
    checkLoginIframe: false,
    flow: "standard",
    responseMode: "query",
  },
  onAuthSuccess,
  onAuthError,
  onTokenExpired,
}) => {
  const [keycloakInstance, setKeycloak] = useState<Keycloak | null>(keycloak);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [noOrgSelected, setNoOrgSelected] = useState(false);
  const [currentOrgSubscription, setCurrentOrgSubscription] =
    useState<CurrentOrgSubscription | null>(null);
  const [memberships, setMemberships] = useState<BootstrapMembership[]>([]);
  const initialized = useRef(false);

  // Helper function to determine if an error is an authentication error
  const isAuthenticationError = useCallback(
    (
      error: Error & { error?: string; response?: { status?: number } },
    ): boolean => {
      // Check for Keycloak auth errors
      if (error.error === "invalid_grant" || error.error === "invalid_token") {
        return true;
      }

      // Check HTTP status codes
      if (error.response?.status === 401 || error.response?.status === 403) {
        return true;
      }

      // Check error message for auth-related keywords
      const errorMessage = error.message?.toLowerCase() || "";
      return (
        errorMessage.includes("unauthorized") ||
        errorMessage.includes("401") ||
        errorMessage.includes("403") ||
        errorMessage.includes("token expired") ||
        errorMessage.includes("invalid token") ||
        errorMessage.includes("authentication failed")
      );
    },
    [],
  );

  // Function to extract capability roles from Keycloak token
  // Capability roles format: {resource}-{action} (e.g., workflow-view, campaign-create)
  const getRolesFromToken = useCallback(
    (tokenParsed: any, orgId: string | null): string[] => {
      if (!tokenParsed) {
        console.warn("⚠️ No token parsed available");
        return [];
      }

      console.log("🔍 Extracting capability roles from token:", {
        tokenClaims: Object.keys(tokenParsed),
        resourceAccess: tokenParsed.resource_access,
        realmAccess: tokenParsed.realm_access,
      });

      const capabilityRoles: string[] = [];

      // Primary: Extract capability roles from realm_access.roles
      // Capability roles follow pattern: {resource}-{action} (e.g., workflow-view, campaign-create)
      if (tokenParsed.realm_access?.roles) {
        const realmRoles = tokenParsed.realm_access.roles;

        // Filter for capability roles - they have hyphen format: resource-action
        // Exclude Keycloak system roles and composite roles
        const systemRolePrefixes = [
          "offline_access",
          "uma_authorization",
          "default-roles-",
        ];
        const capabilityRolesFromRealm = realmRoles.filter((role: string) => {
          // Capability roles follow pattern: {resource}-{action} or {resource}-{action}-{modifier}
          const isCapabilityRole =
            /^[a-z]+-[a-z]+/.test(role.toLowerCase()) ||
            /^[a-z]+-[a-z]+-[a-z]+/.test(role.toLowerCase());

          // Exclude system roles
          const isSystemRole = systemRolePrefixes.some(prefix =>
            role.startsWith(prefix),
          );

          // Exclude composite roles (they end with -capabilities)
          const isCompositeRole = role.toLowerCase().endsWith("-capabilities");

          // Exclude membership roles (org-owner, org-admin, etc.)
          const isMembershipRole =
            (role.toLowerCase().startsWith("org-") &&
              !role.toLowerCase().includes("-")) ||
            [
              "owner",
              "admin",
              "supervisor",
              "agent",
              "developer",
              "marketing",
              "reporter",
            ].includes(role.toLowerCase());

          return (
            isCapabilityRole &&
            !isSystemRole &&
            !isCompositeRole &&
            !isMembershipRole
          );
        });

        if (capabilityRolesFromRealm.length > 0) {
          console.log(
            "✅ Found capability roles from realm_access.roles:",
            capabilityRolesFromRealm,
          );
          capabilityRoles.push(...capabilityRolesFromRealm);
        }
      }

      // Fallback: Check resource_access for client-specific capability roles
      const clientId =
        keycloakInstance?.clientId ||
        import.meta.env.VITE_API_AUTH_CLIENT_ID ||
        import.meta.env.VITE_KEYCLOAK_CLIENT_ID ||
        "botbat-console-stg";

      console.log("🔍 Checking resource_access for clientId:", clientId);

      if (tokenParsed.resource_access?.[clientId]?.roles) {
        const clientRoles = tokenParsed.resource_access[clientId].roles;

        // Filter for capability roles
        const capabilityClientRoles = clientRoles.filter((role: string) => {
          const isCapabilityRole = /^[a-z]+-[a-z]+/.test(role.toLowerCase());
          const isCompositeRole = role.toLowerCase().endsWith("-capabilities");
          return isCapabilityRole && !isCompositeRole;
        });

        if (capabilityClientRoles.length > 0) {
          console.log(
            `✅ Found capability roles from resource_access.${clientId}:`,
            capabilityClientRoles,
          );
          capabilityRoles.push(...capabilityClientRoles);
        }
      }

      // Remove duplicates and normalize to lowercase
      const uniqueCapabilityRoles = Array.from(
        new Set(capabilityRoles.map(r => r.toLowerCase())),
      );
      console.log(
        "📋 Final extracted capability roles from token:",
        uniqueCapabilityRoles,
      );

      return uniqueCapabilityRoles;
    },
    [keycloakInstance],
  );

  // Function to update CASL permissions based on organization capability roles
  const updateCASLPermissionsFromOrganization = useCallback(
    async (userId: string | undefined, orgs: Organization[]) => {
      if (!orgs || orgs.length === 0) {
        console.warn("⚠️ Cannot update CASL permissions: no organizations");
        // Set to minimal permissions if no organizations
        const defaultCapabilities = membershipRoleToCapabilities("reporter");
        caslStore.setRoles(defaultCapabilities);
        return;
      }

      try {
        // Get current organization from localStorage
        const currentOrgJson = localStorage.getItem("currentOrganization");
        let currentOrg: Organization | null = null;

        try {
          currentOrg = currentOrgJson ? JSON.parse(currentOrgJson) : null;
        } catch (error) {
          console.error(
            "❌ Failed to parse currentOrganization from localStorage:",
            error,
          );
          // Use first organization as fallback
          currentOrg = orgs[0] || null;
        }

        const orgId = currentOrg?.id || orgs[0]?.id;

        if (!orgId) {
          console.warn(
            "⚠️ No organization ID found, skipping CASL permission update",
          );
          // Set to minimal permissions
          const defaultCapabilities = membershipRoleToCapabilities("reporter");
          caslStore.setRoles(defaultCapabilities);
          return;
        }

        // Validate user is member of organization
        const userIsMember = orgs.some(org => org.id === orgId);
        if (!userIsMember) {
          console.warn(
            `⚠️ User is not a member of organization ${orgId}, clearing permissions`,
          );
          caslStore.setRoles([]);
          return;
        }

        console.log("🔍 Updating CASL permissions for organization:", orgId);

        // First, try to get capability roles from token
        const tokenParsed = keycloakInstance?.tokenParsed;
        if (tokenParsed) {
          console.log(
            "🔑 Attempting to extract capability roles from Keycloak token...",
          );
          const capabilityRoles = getRolesFromToken(tokenParsed, orgId);

          if (capabilityRoles.length > 0) {
            console.log("✅ Found capability roles in token:", capabilityRoles);
            console.log(
              "🔐 Updating CASL permissions with capability roles from token",
            );
            caslStore.setRoles(capabilityRoles);

            // Verify permissions were set correctly
            const ability = caslStore.ability;
            const canManageAll = ability.can("manage", "all");
            console.debug(
              "✅ CASL permissions updated from token - can manage all:",
              canManageAll,
            );
            console.debug("📊 Current CASL ability rules:", ability.rules);
            return;
          } else {
            console.warn(
              "⚠️ No capability roles found in token, falling back to API",
            );
          }
        } else {
          console.warn("⚠️ No token parsed available, falling back to API");
        }

        // Fallback: Fetch from API if not in token
        if (!userId) {
          console.warn("⚠️ Cannot fetch from API: missing userId");
          return;
        }

        console.log(
          "🔍 Fetching membership role from API for organization:",
          orgId,
        );
        const accountService = new AccountService();

        try {
          const membershipRole =
            await accountService.getCurrentUserMembershipRole(orgId, userId);

          if (membershipRole) {
            console.log("✅ Found membership role from API:", membershipRole);

            // Map membership role to capability roles
            const capabilityRoles =
              membershipRoleToCapabilities(membershipRole);
            console.log(
              "🔄 Mapped membership role to capability roles:",
              capabilityRoles,
            );

            console.log(
              "🔐 Updating CASL permissions with capability roles from API fallback",
            );
            caslStore.setRoles(capabilityRoles);

            // Verify permissions were set correctly
            const ability = caslStore.ability;
            const canManageAll = ability.can("manage", "all");
            console.debug(
              "✅ CASL permissions updated from API fallback - can manage all:",
              canManageAll,
            );
            console.debug("📊 Current CASL ability rules:", ability.rules);
          } else {
            console.warn(
              "⚠️ No membership role found for organization:",
              orgId,
            );
            // Fallback to minimal permissions (reporter)
            console.log("🔄 Using default reporter capabilities");
            const defaultCapabilities =
              membershipRoleToCapabilities("reporter");
            caslStore.setRoles(defaultCapabilities);
          }
        } catch (error) {
          console.error("❌ Failed to fetch membership role from API:", error);
          // Keep current permissions or set to minimal
          console.warn("⚠️ Keeping current permissions or setting to minimal");
          const defaultCapabilities = membershipRoleToCapabilities("reporter");
          caslStore.setRoles(defaultCapabilities);
        }
      } catch (error) {
        console.error("❌ Failed to update CASL permissions:", error);
      }
    },
    [keycloakInstance, getRolesFromToken],
  );

  const updateOrganizationState = useCallback((orgs: Organization[]) => {
    const currentOrg = localStorage.getItem("currentOrganization");
    const parsedCurrentOrg = currentOrg ? JSON.parse(currentOrg) : null;

    // If no organizations at all, clear cached org and set no org state
    if (orgs.length === 0) {
      console.log("🚫 No organizations found, clearing cache");
      localStorage.removeItem("currentOrganization");
      localStorage.removeItem("userOrganizations");
      setNoOrgSelected(false); // Not "no org selected" - there are no orgs at all
    } else if (orgs.length === 1) {
      if (!parsedCurrentOrg || parsedCurrentOrg.id !== orgs[0].id) {
        localStorage.setItem("currentOrganization", JSON.stringify(orgs[0]));
      }
      setNoOrgSelected(false);
    } else if (orgs.length > 1 && !parsedCurrentOrg) {
      setNoOrgSelected(true);
    } else {
      setNoOrgSelected(false);
    }
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    let tokenRefreshInterval: NodeJS.Timeout | null = null;

    const initKeycloak = async () => {
      try {
        const auth = await keycloak.init(initOptions);
        if (!auth) {
          await keycloak.login();
          return;
        }

        if (auth) {
          let userProfile: KeycloakProfile;
          try {
            userProfile = await keycloak.loadUserProfile();
          } catch (error) {
            // If loading user profile fails, it's likely an auth issue
            console.error("❌ Failed to load user profile:", error);
            const authError = error as Error;
            onAuthError?.(authError);
            // Only re-authenticate if it's actually an auth error
            if (isAuthenticationError(authError)) {
              setAuthenticated(false);
              setUser(null);
              setOrganizations([]);
              setNoOrgSelected(false);
              setLoading(false);
              return;
            }
            // For other errors, don't re-authenticate - just log and continue with token data
            console.warn(
              "⚠️ Continuing despite profile load error, using token data",
            );
            userProfile = {
              id: keycloak.tokenParsed?.sub || "",
              username: keycloak.tokenParsed?.preferred_username || "",
              email: keycloak.tokenParsed?.email || "",
            } as KeycloakProfile;
          }

          // Extract roles from token (will be set properly by updateCASLPermissionsFromOrganization)
          const tokenParsed = keycloak.tokenParsed;
          console.debug("🔑 Keycloak token parsed:", {
            availableClaims: Object.keys(tokenParsed || {}),
            resourceAccess: tokenParsed?.resource_access,
            realmAccess: tokenParsed?.realm_access,
            clientId: keycloak.clientId,
          });

          const userData = {
            id: userProfile.id,
            username: userProfile.username,
            email: userProfile.email,
            roles: tokenParsed?.realm_access?.roles ?? [],
            token: keycloak.token || undefined,
          };

          setUser(userData);
          setAuthenticated(true);
          setKeycloak(keycloak);

          // Use bootstrap endpoint to fetch all data in one call
          // This replaces: fetchUserOrganizations + getUserMemberships + payment-status
          let orgs: Organization[] = [];
          try {
            console.debug("🚀 Fetching bootstrap data...");
            const accountService = new AccountService();
            const bootstrapData = await accountService.getBootstrapData();
            console.debug("✅ Bootstrap data received:", {
              orgsCount: bootstrapData.organizations?.length,
              hasCurrentOrg: !!bootstrapData.currentOrganization,
            });

            // Map bootstrap organizations to Organization type
            orgs = bootstrapData.organizations.map(org => ({
              id: org.id,
              name: org.name,
              status: org.status,
              currency: org.currency,
              timezone: org.timezone,
              industry: org.industry,
            })) as Organization[];

            setOrganizations(orgs);
            updateOrganizationState(orgs);

            // store memberships so other components can access per-org subscription info
            setMemberships(bootstrapData.memberships || []);

            // Set subscription status from bootstrap (avoids separate payment-status call)
            if (bootstrapData.currentOrganization) {
              setCurrentOrgSubscription({
                subscription: bootstrapData.currentOrganization.subscription,
                needsPayment: bootstrapData.currentOrganization.needsPayment,
                checkoutUrl: bootstrapData.currentOrganization.checkoutUrl,
                membershipRole:
                  bootstrapData.currentOrganization.membership.role,
              });

              // Update CASL permissions from bootstrap membership role
              const membershipRole =
                bootstrapData.currentOrganization.membership.role;
              if (membershipRole) {
                console.debug(
                  "🔐 Setting CASL permissions from bootstrap role:",
                  membershipRole,
                );
                const capabilityRoles =
                  membershipRoleToCapabilities(membershipRole);
                caslStore.setRoles(capabilityRoles);
              }
            } else if (bootstrapData.memberships?.length > 0) {
              // If no current org but have memberships, use first one
              const firstMembership = bootstrapData.memberships[0];
              setCurrentOrgSubscription({
                subscription: firstMembership.subscription,
                needsPayment: false,
                membershipRole: firstMembership.role,
              });

              const capabilityRoles = membershipRoleToCapabilities(
                firstMembership.role,
              );
              caslStore.setRoles(capabilityRoles);
            }

            // Cache organizations in localStorage as fallback
            try {
              localStorage.setItem("userOrganizations", JSON.stringify(orgs));
            } catch (storageError) {
              console.warn(
                "⚠️ Failed to cache organizations in localStorage:",
                storageError,
              );
            }
          } catch (error) {
            // Bootstrap fetch failure - try fallback to old method
            console.error(
              "❌ Failed to fetch bootstrap data, falling back to legacy method:",
              error,
            );

            try {
              orgs = await new AccountService().fetchUserOrganizations();
              setOrganizations(orgs);
              updateOrganizationState(orgs);
            } catch (fallbackError) {
              console.error(
                "❌ Legacy fetch also failed (non-critical):",
                fallbackError,
              );
              // Try to get organizations from localStorage as fallback
              try {
                const storedOrgs = localStorage.getItem("userOrganizations");
                if (storedOrgs) {
                  orgs = JSON.parse(storedOrgs);
                  setOrganizations(orgs);
                  updateOrganizationState(orgs);
                  console.log(
                    "✅ Using cached organizations from localStorage",
                  );
                }
              } catch (parseError) {
                console.warn("⚠️ Could not parse cached organizations");
              }
            }

            // Update CASL permissions via old method
            updateCASLPermissionsFromOrganization(userData.id, orgs).catch(
              permError => {
                console.error(
                  "❌ Failed to update CASL permissions (non-critical):",
                  permError,
                );
              },
            );
          }

          // Setup token refresh handler with better error handling
          keycloak.onTokenExpired = () => {
            console.log("🔄 Token expired, refreshing...");
            keycloak
              .updateToken(30)
              .then(refreshed => {
                if (refreshed) {
                  console.log("✅ Token refreshed successfully");
                  // Re-extract roles from new token and update permissions
                  const newTokenParsed = keycloak.tokenParsed;
                  if (newTokenParsed && userData.id && orgs.length > 0) {
                    console.log("🔄 Updating permissions after token refresh");
                    updateCASLPermissionsFromOrganization(
                      userData.id,
                      orgs,
                    ).catch(error => {
                      console.error(
                        "❌ Failed to update permissions after token refresh:",
                        error,
                      );
                      // Don't re-authenticate for permission update failures
                    });
                  }
                } else {
                  console.warn(
                    "⚠️ Token refresh returned false (token may still be valid)",
                  );
                }
              })
              .catch(error => {
                // Only logout/re-authenticate for actual auth errors, not network/service errors
                if (isAuthenticationError(error)) {
                  console.error(
                    "❌ Authentication error during token refresh:",
                    error,
                  );
                  onTokenExpired?.();
                  keycloak.logout();
                } else {
                  // For network/service errors, just log and keep existing session
                  console.error(
                    "❌ Network/service error during token refresh (non-critical):",
                    error,
                  );
                  console.warn(
                    "⚠️ Keeping existing session - token may still be valid",
                  );
                  // Don't logout - user can continue with existing token
                }
              });
          };

          // Setup auto token refresh interval with better error handling
          tokenRefreshInterval = setInterval(() => {
            keycloak
              .updateToken(70)
              .then(refreshed => {
                if (refreshed) {
                  console.log("✅ Auto token refresh successful");
                  // Re-extract roles from refreshed token and update permissions
                  const refreshedTokenParsed = keycloak.tokenParsed;
                  if (refreshedTokenParsed && userData.id && orgs.length > 0) {
                    updateCASLPermissionsFromOrganization(
                      userData.id,
                      orgs,
                    ).catch(error => {
                      console.error(
                        "❌ Failed to update permissions after auto token refresh:",
                        error,
                      );
                      // Don't re-authenticate for permission update failures
                    });
                  }
                }
              })
              .catch(error => {
                // Only clear token for actual auth errors
                if (isAuthenticationError(error)) {
                  console.error(
                    "❌ Authentication error during auto token refresh:",
                    error,
                  );
                  keycloak.clearToken();
                  onTokenExpired?.();
                } else {
                  // For network/service errors, just log - don't clear token
                  console.error(
                    "❌ Network/service error during auto token refresh (non-critical):",
                    error,
                  );
                  console.warn(
                    "⚠️ Keeping existing token - session remains valid",
                  );
                }
              });
          }, 60000);

          onAuthSuccess?.();
        } else {
          setAuthenticated(false);
          setUser(null);
          setOrganizations([]);
          setNoOrgSelected(false);
        }
      } catch (error) {
        // Distinguish between auth errors and other errors
        if (isAuthenticationError(error as Error)) {
          console.error(
            "❌ Authentication error during Keycloak initialization:",
            error,
          );
          onAuthError?.(authError);
          setAuthenticated(false);
          setUser(null);
          setOrganizations([]);
          setNoOrgSelected(false);
        } else {
          // For non-auth errors (network, service issues), handle more gracefully
          console.error(
            "❌ Non-authentication error during Keycloak initialization:",
            error,
          );
          console.warn(
            "⚠️ Error may be temporary - checking if user is still authenticated",
          );

          // Check if we still have a valid token
          if (keycloak.authenticated && keycloak.token) {
            console.log(
              "✅ User still appears authenticated - continuing with existing session",
            );
            // Try to proceed with existing authentication
            try {
              const tokenParsed = keycloak.tokenParsed;
              if (tokenParsed?.sub) {
                const userData = {
                  id: tokenParsed.sub,
                  username: tokenParsed.preferred_username || "",
                  email: tokenParsed.email || "",
                  roles: tokenParsed?.realm_access?.roles ?? [],
                  token: keycloak.token || undefined,
                };
                setUser(userData);
                setAuthenticated(true);
                setKeycloak(keycloak);
                // Try to fetch organizations in background
                new AccountService()
                  .fetchUserOrganizations()
                  .then(orgs => {
                    setOrganizations(orgs);
                    updateOrganizationState(orgs);
                    updateCASLPermissionsFromOrganization(
                      userData.id,
                      orgs,
                    ).catch(err => {
                      console.error("❌ Failed to update permissions:", err);
                    });
                  })
                  .catch(err => {
                    console.error("❌ Failed to fetch organizations:", err);
                  });
              }
            } catch (recoveryError) {
              console.error("❌ Failed to recover session:", recoveryError);
              setAuthenticated(false);
              setUser(null);
              setOrganizations([]);
              setNoOrgSelected(false);
            }
          } else {
            // No valid token, must re-authenticate
            setAuthenticated(false);
            setUser(null);
            setOrganizations([]);
            setNoOrgSelected(false);
          }
        }
      } finally {
        setLoading(false);
      }
    };
    initKeycloak();

    return () => {
      if (tokenRefreshInterval) {
        clearInterval(tokenRefreshInterval);
      }
    };
  }, [
    updateOrganizationState,
    updateCASLPermissionsFromOrganization,
    onAuthError,
    onAuthSuccess,
    onTokenExpired,
    isAuthenticationError,
  ]);

  // Listen for organization changes from OTHER tabs/windows only
  // Note: Storage events only fire when localStorage is changed from a DIFFERENT document
  // They don't fire for changes made in the same tab, so this won't cause loops
  useEffect(() => {
    if (!authenticated || !user?.id || organizations.length === 0) {
      return;
    }

    const handleStorageChange = async (e: StorageEvent) => {
      // Only react to currentOrganization changes from other tabs
      if (e.key === "currentOrganization" && e.newValue !== e.oldValue) {
        console.log(
          "🔄 Organization changed in another tab, refreshing bootstrap data...",
        );
        // Refresh bootstrap to get new org's subscription status and permissions
        try {
          const accountService = new AccountService();
          const bootstrapData = await accountService.getBootstrapData();

          if (bootstrapData.currentOrganization) {
            setCurrentOrgSubscription({
              subscription: bootstrapData.currentOrganization.subscription,
              needsPayment: bootstrapData.currentOrganization.needsPayment,
              checkoutUrl: bootstrapData.currentOrganization.checkoutUrl,
              membershipRole: bootstrapData.currentOrganization.membership.role,
            });

            // Update CASL permissions
            const membershipRole =
              bootstrapData.currentOrganization.membership.role;
            if (membershipRole) {
              const capabilityRoles =
                membershipRoleToCapabilities(membershipRole);
              caslStore.setRoles(capabilityRoles);
            }
          }
        } catch (error) {
          console.error("❌ Failed to refresh bootstrap on org change:", error);
          // Fall back to just updating CASL permissions
          await updateCASLPermissionsFromOrganization(user.id, organizations);
        }
      }
    };

    // Listen for storage events (only fires for changes from OTHER tabs/windows)
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, [
    authenticated,
    user?.id,
    organizations,
    updateCASLPermissionsFromOrganization,
  ]);

  const login = useCallback((): void => {
    if (keycloakInstance) {
      keycloakInstance.login();
    }
  }, [keycloakInstance]);

  const logout = useCallback((): void => {
    if (keycloakInstance) {
      localStorage.removeItem("currentOrganization");
      keycloakInstance.logout({ redirectUri: window.location.origin });
    }
  }, [keycloakInstance]);

  const registerUser = useCallback((): void => {
    if (keycloakInstance) {
      keycloakInstance.register();
    }
  }, [keycloakInstance]);

  const refreshOrganizations = useCallback(async (): Promise<void> => {
    console.log("🔄 Refreshing organizations list...");
    try {
      const orgs = await new AccountService().fetchUserOrganizations();
      console.log("📝 Fetched organizations:", orgs);
      setOrganizations(orgs);
      updateOrganizationState(orgs);

      // Cache organizations in localStorage as fallback
      try {
        localStorage.setItem("userOrganizations", JSON.stringify(orgs));
      } catch (storageError) {
        console.warn(
          "⚠️ Failed to cache organizations in localStorage:",
          storageError,
        );
      }

      // Update CASL permissions when organizations refresh
      if (user?.id) {
        await updateCASLPermissionsFromOrganization(user.id, orgs);
      }

      console.log("✅ Organizations refreshed successfully");
    } catch (error) {
      // Don't re-authenticate for organization refresh failures unless it's an auth error
      if (isAuthenticationError(error as Error)) {
        console.error(
          "❌ Authentication error while refreshing organizations:",
          error,
        );
        // Only logout if it's a real auth error (401 specifically)
        const orgError = error as Error & { response?: { status?: number } };
        if (orgError.response?.status === 401) {
          keycloakInstance?.logout();
        }
      } else {
        console.error(
          "❌ Failed to refresh organizations (non-critical):",
          error,
        );
        // Try to use cached organizations as fallback
        try {
          const cachedOrgs = localStorage.getItem("userOrganizations");
          if (cachedOrgs) {
            const orgs = JSON.parse(cachedOrgs);
            console.log("⚠️ Using cached organizations as fallback");
            setOrganizations(orgs);
            updateOrganizationState(orgs);
          }
        } catch (parseError) {
          console.warn("⚠️ Could not use cached organizations");
        }
      }
    }
  }, [
    updateOrganizationState,
    user?.id,
    updateCASLPermissionsFromOrganization,
    keycloakInstance,
    isAuthenticationError,
  ]);
  const refreshPermissions = useCallback(async (): Promise<void> => {
    if (user?.id && organizations.length > 0) {
      console.log("🔄 Manually refreshing CASL permissions...");
      await updateCASLPermissionsFromOrganization(user.id, organizations);
    }
  }, [user?.id, organizations, updateCASLPermissionsFromOrganization]);

  // Refresh bootstrap data (organizations + subscription status)
  // Call this after organization switch or after payment completion
  const refreshBootstrap = useCallback(async (): Promise<void> => {
    console.log("🔄 Refreshing bootstrap data...");
    try {
      const accountService = new AccountService();
      const bootstrapData = await accountService.getBootstrapData();
      console.log("✅ Bootstrap data refreshed:", {
        orgsCount: bootstrapData.organizations?.length,
        hasCurrentOrg: !!bootstrapData.currentOrganization,
      });

      // Update organizations
      const orgs = bootstrapData.organizations.map(org => ({
        id: org.id,
        name: org.name,
        status: org.status,
        currency: org.currency,
        timezone: org.timezone,
        industry: org.industry,
      })) as Organization[];

      setOrganizations(orgs);
      updateOrganizationState(orgs);

      // Update subscription status
      if (bootstrapData.currentOrganization) {
        setCurrentOrgSubscription({
          subscription: bootstrapData.currentOrganization.subscription,
          needsPayment: bootstrapData.currentOrganization.needsPayment,
          checkoutUrl: bootstrapData.currentOrganization.checkoutUrl,
          membershipRole: bootstrapData.currentOrganization.membership.role,
        });

        // Update CASL permissions
        const membershipRole =
          bootstrapData.currentOrganization.membership.role;
        if (membershipRole) {
          const capabilityRoles = membershipRoleToCapabilities(membershipRole);
          caslStore.setRoles(capabilityRoles);
        }
      }

      // Cache organizations
      try {
        localStorage.setItem("userOrganizations", JSON.stringify(orgs));
      } catch (storageError) {
        console.warn("⚠️ Failed to cache organizations:", storageError);
      }

      console.log("✅ Bootstrap data refreshed successfully");
    } catch (error) {
      console.error("❌ Failed to refresh bootstrap data:", error);
      // Fall back to legacy refresh
      await refreshOrganizations();
    }
  }, [updateOrganizationState, refreshOrganizations]);

  const value = {
    keycloakInstance,
    authenticated,
    user,
    loading,
    login,
    logout,
    registerUser,
    organizations,
    noOrgSelected,
    currentOrgSubscription,
    memberships,
    refreshOrganizations,
    refreshPermissions,
    refreshBootstrap,
  };

  if (loading) {
    return <Loader />;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
